import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { MdFlag, MdDescription, MdAttachFile, MdPeople, MdHistory, MdExpandMore, MdExpandLess, MdComment, MdFileDownload, MdEdit, MdVisibility } from 'react-icons/md';
import { setSelectedIncident } from '_redux/slices/incidents/incidents';
import { getEmployeeList, selectApprovedEmployee } from '_redux/slices/timeline/employees';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { subscribeToIncident, updateIncidentStatus, returnIncident, updateIncident, deleteIncident } from '_services/incident-service';
import { generateIncidentReportPdf } from './incident-pdf';

//#region helpers
// Handles Firestore Timestamp objects (.toDate()) and the raw { seconds, nanoseconds } shape.
const toJsDate = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    const d = new Date(ts);
    return isNaN(d) ? null : d;
};

const formatDate = (ts) => {
    const d = toJsDate(ts);
    return d ? d.toLocaleDateString() : '—';
};

const formatDateTime = (ts) => {
    const d = toJsDate(ts);
    return d ? d.toLocaleString() : '—';
};

//#endregion

//#region edit-mode input styles
const textareaStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    color: '#374151',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    resize: 'vertical',
    minHeight: 84,
    lineHeight: 1.5,
};

const editLabelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    display: 'block',
};
//#endregion

//#region style constants — aligned with flight-information-card.js conventions
const CARD_STYLE = {
    marginBottom: 16,
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    backgroundColor: 'white',
};

const CONTENT_STYLE = { padding: 16 };

const ROW_STYLE = {
    display: 'flex',
    gap: 24,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
};

const LABEL_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
};

const VALUE_STYLE = {
    fontSize: 14,
    color: '#374151',
    margin: 0,
    fontWeight: 500,
};

const tableContainerStyle = {
    overflowX: 'auto',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    fontSize: 14,
};

const thStyle = {
    backgroundColor: '#f8fafc',
    color: '#374151',
    fontWeight: 600,
    padding: '10px 14px',
    textAlign: 'left',
    borderBottom: '2px solid #e5e7eb',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    position: 'sticky',
    top: 0,
};

const tdStyle = {
    padding: '10px 14px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    verticalAlign: 'middle',
};
//#endregion

//#region sub-components
const InfoItem = ({ label, children }) => (
    <div style={{ flex: 1, minWidth: 140 }}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{children || '—'}</div>
    </div>
);

const CardHeader = ({ color, icon: Icon, title, expanded, onToggle }) => (
    <Card.Header
        style={{
            backgroundColor: color,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            cursor: onToggle ? 'pointer' : 'default',
            userSelect: 'none',
        }}
        onClick={onToggle}
    >
        {Icon && <Icon size={18} color="white" />}
        <p style={{ margin: 0, color: 'white', fontSize: 15, fontWeight: 600, flex: 1 }}>{title}</p>
        {onToggle && (
            <span style={{ display: 'flex', color: 'white' }}>
                {expanded ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
            </span>
        )}
    </Card.Header>
);

const CollapsibleCard = ({ color, icon, title, defaultExpanded = false, children }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    return (
        <Card style={CARD_STYLE}>
            <CardHeader
                color={color}
                icon={icon}
                title={title}
                expanded={expanded}
                onToggle={() => setExpanded((v) => !v)}
            />
            {expanded && children}
        </Card>
    );
};

// Incident-level statuses
const STATUS_MAP = {
    open: { background: '#E5E7EB', color: '#374151', label: 'Awaiting Acknowledgement' },
    in_progress: { background: '#DBEAFE', color: '#1E40AF', label: 'Acknowledged — In Progress' },
    fully_acknowledged: { background: '#FEF3C7', color: '#92400E', label: 'Acknowledged — Pending Approval' },
    returned: { background: '#FEE2E2', color: '#991B1B', label: 'Returned to Agent' },
    closed: { background: '#D1FAE5', color: '#065F46', label: 'Approved & Complete' },
};

// Employee-level ack statuses
const ACK_STATUS_MAP = {
    pending: { background: '#E5E7EB', color: '#374151', label: 'Pending' },
    acknowledged: { background: '#D1FAE5', color: '#065F46', label: 'Acknowledged' },
};

const StatusBadge = ({ status, ack }) => {
    const map = ack ? ACK_STATUS_MAP : STATUS_MAP;
    const s = map[status] || (ack ? ACK_STATUS_MAP.pending : STATUS_MAP.open);
    return (
        <span style={{
            background: s.background,
            color: s.color,
            padding: '3px 10px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
        }}>
            {s.label}
        </span>
    );
};
//#endregion

const IncidentDetail = ({ incidentId, onBack }) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const employees = useSelector(selectApprovedEmployee) ?? [];
    const [incident, setIncident] = useState(null);
    const [closing, setClosing] = useState(false);
    const [acknowledging, setAcknowledging] = useState(false);
    const [unapproving, setUnapproving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [returning, setReturning] = useState(false);
    const [showReturn, setShowReturn] = useState(false);
    const [returnComment, setReturnComment] = useState('');
    const [returnRecipients, setReturnRecipients] = useState([]);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [draft, setDraft] = useState(null);

    useEffect(() => {
        if (!incidentId) return;
        const unsubscribe = subscribeToIncident(incidentId, setIncident);
        return () => unsubscribe();
    }, [incidentId]);

    useEffect(() => {
        const iata = dbUser?.details?.authority;
        if (iata && employees.length === 0) dispatch(getEmployeeList(iata));
    }, [dbUser, dispatch, employees.length]);

    const employeeNameMap = useMemo(() => {
        const m = {};
        employees.forEach((e) => {
            if (e?.employeeId) m[e.employeeId] = e.title ?? e.employeeId;
        });
        return m;
    }, [employees]);

    const handleClose = async () => {
        setClosing(true);
        try {
            await updateIncidentStatus(incidentId, 'closed');
        } finally {
            setClosing(false);
        }
    };

    // Reviewer acknowledgement — signals to the agent who raised the report that
    // the office has seen it and is dealing with it, without marking it complete.
    const handleAcknowledge = async () => {
        setAcknowledging(true);
        try {
            await updateIncidentStatus(incidentId, 'in_progress');
        } finally {
            setAcknowledging(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteIncident(incidentId);
            handleBack();
        } finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    };

    const assignedEmployees = incident?.assignedEmployees ?? [];

    const openReturn = () => {
        // Default to returning the report to every assigned agent.
        setReturnRecipients(assignedEmployees);
        setShowReturn(true);
    };

    const toggleRecipient = (empId) => {
        setReturnRecipients((prev) =>
            prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
        );
    };

    const handleReturn = async () => {
        const comment = returnComment.trim();
        if (!comment || returnRecipients.length === 0) return;
        setReturning(true);
        try {
            // When every agent is selected, send no explicit list (backend
            // treats an omitted list as "all assigned").
            const allSelected = returnRecipients.length === assignedEmployees.length;
            await returnIncident(incidentId, comment, allSelected ? undefined : returnRecipients);
            setShowReturn(false);
            setReturnComment('');
        } finally {
            setReturning(false);
        }
    };

    const handleExportPdf = () => {
        generateIncidentReportPdf(incident, employeeNameMap);
    };

    const startEdit = () => {
        setSaveError('');
        setDraft({
            issue: incident.issue ?? '',
            actionTaken: incident.actionTaken ?? '',
            recommendation: incident.recommendation ?? '',
        });
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setDraft(null);
        setSaveError('');
    };

    const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    const handleSaveEdit = async () => {
        setSaving(true);
        setSaveError('');
        try {
            await updateIncident(incidentId, {
                issue: draft.issue.trim(),
                actionTaken: draft.actionTaken.trim(),
                recommendation: draft.recommendation.trim(),
            });
            setEditing(false);
            setDraft(null);
        } catch (e) {
            setSaveError(e.message || 'Failed to save changes.');
        } finally {
            setSaving(false);
        }
    };

    const handleUnapprove = async () => {
        setUnapproving(true);
        try {
            const ackEntries = Object.values(incident.acknowledgements ?? {});
            const allAcked = ackEntries.length > 0 && ackEntries.every((a) => a.status === 'acknowledged');
            await updateIncidentStatus(incidentId, allAcked ? 'fully_acknowledged' : 'open');
        } finally {
            setUnapproving(false);
        }
    };

    const handleBack = () => {
        dispatch(setSelectedIncident(null));
        onBack?.();
    };

    if (!incident) {
        return (
            <div style={{ padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                Loading incident...
            </div>
        );
    }

    const ackEntries = Object.entries(incident.acknowledgements ?? {});
    const isClosed = incident.status === 'closed';
    const headerColor = isClosed ? '#198754' : '#6b7280';

    // Status-change audit trail, newest first.
    const sortedEvents = [...(incident.eventLog ?? [])].sort(
        (a, b) => (toJsDate(b.timestamp)?.getTime() ?? 0) - (toJsDate(a.timestamp)?.getTime() ?? 0)
    );

    // Reviewer feedback trail (sent back to the agent), newest first.
    const sortedComments = [...(incident.reviewComments ?? [])].sort(
        (a, b) => (toJsDate(b.timestamp)?.getTime() ?? 0) - (toJsDate(a.timestamp)?.getTime() ?? 0)
    );

    // Most recent office acknowledgement (latest 'in_progress' event), surfaced
    // in the header so reviewers see who picked the report up and when.
    const ackEvent = sortedEvents.find((ev) => ev.status === 'in_progress');

    return (
        <>
            {/* Top bar — back, export, edit, status */}
            <Row className="mt-3 mb-3 align-items-center">
                <Col>
                    <Button variant="outline-secondary" size="sm" onClick={handleBack} disabled={editing}>
                        ← Back to list
                    </Button>
                </Col>
                <Col xs="auto" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleExportPdf}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                        <MdFileDownload size={16} />
                        Export Report (PDF)
                    </Button>
                    {!editing && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={startEdit}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                            <MdEdit size={16} />
                            Edit
                        </Button>
                    )}
                    <StatusBadge status={incident.status} />
                </Col>
            </Row>

            {/* Office acknowledgement attribution — who picked it up and when */}
            {incident.status === 'in_progress' && ackEvent && (
                <Row className="mb-3">
                    <Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            color: '#1E40AF',
                            background: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                            borderRadius: 6,
                            padding: '4px 10px',
                        }}>
                            <MdVisibility size={14} />
                            Acknowledged by {ackEvent.author?.name || ackEvent.author?.email || 'Reviewer'} · {formatDateTime(ackEvent.timestamp)}
                        </span>
                    </Col>
                </Row>
            )}

            {/* ── Edit panel (reviewer adjustments) ── */}
            {editing && draft && (
                <div style={{ marginBottom: 8 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: '#EFF6FF', border: '1px solid #BFDBFE',
                        borderRadius: 8, padding: '10px 14px', marginBottom: 12,
                    }}>
                        <MdEdit size={18} color="#2563eb" />
                        <span style={{ fontSize: 14, color: '#1e3a8a', fontWeight: 600 }}>
                            Editing report details — changes are saved to the report and recorded in the event log.
                        </span>
                    </div>

                    {/* Details — issue / action taken / recommendation */}
                    <Card style={CARD_STYLE}>
                        <CardHeader color={headerColor} icon={MdDescription} title="Details" />
                        <Card.Body style={CONTENT_STYLE}>
                            <div style={{ marginBottom: 14 }}>
                                <label style={editLabelStyle}>Issue</label>
                                <textarea style={textareaStyle} value={draft.issue} onChange={(e) => setField('issue', e.target.value)} />
                            </div>
                            <div style={{ marginBottom: 14 }}>
                                <label style={editLabelStyle}>Action Taken</label>
                                <textarea style={textareaStyle} value={draft.actionTaken} onChange={(e) => setField('actionTaken', e.target.value)} />
                            </div>
                            <div>
                                <label style={editLabelStyle}>Recommendation</label>
                                <textarea style={textareaStyle} value={draft.recommendation} onChange={(e) => setField('recommendation', e.target.value)} />
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            )}

            {!editing && (<>
                {/* ── Card 1: Incident summary (auto-folded) ── */}
                <CollapsibleCard color={headerColor} icon={MdFlag} title={incident.title} defaultExpanded={false}>
                    <Card.Body style={CONTENT_STYLE}>
                        <div style={ROW_STYLE}>
                            <InfoItem label="Incident Type">{incident.incidentType}</InfoItem>
                            <InfoItem label="Incident Date">{formatDate(incident.date)}</InfoItem>
                            <InfoItem label="Reported At">{formatDateTime(incident.createdAt)}</InfoItem>
                            <InfoItem label="Flight Number">{incident.flightNumber}</InfoItem>
                            <InfoItem label="Location">{incident.location}</InfoItem>
                        </div>
                    </Card.Body>
                </CollapsibleCard>

                {/* ── Card 2: Overview (auto-folded) ── */}
                <CollapsibleCard color={headerColor} icon={MdDescription} title="Overview" defaultExpanded={false}>
                    <Card.Body style={CONTENT_STYLE}>
                        <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {incident.description || '—'}
                        </p>
                    </Card.Body>
                </CollapsibleCard>

                {/* ── Card 3: Details (auto-folded) ── */}
                {(incident.issue || incident.actionTaken || incident.recommendation) && (
                    <CollapsibleCard color={headerColor} icon={MdDescription} title="Details" defaultExpanded={false}>
                        <Card.Body style={CONTENT_STYLE}>
                            {incident.issue && (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={LABEL_STYLE}>Issue</div>
                                    <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                        {incident.issue}
                                    </p>
                                </div>
                            )}
                            {incident.actionTaken && (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={LABEL_STYLE}>Action Taken</div>
                                    <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                        {incident.actionTaken}
                                    </p>
                                </div>
                            )}
                            {incident.recommendation && (
                                <div>
                                    <div style={LABEL_STYLE}>Recommendation</div>
                                    <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                        {incident.recommendation}
                                    </p>
                                </div>
                            )}
                        </Card.Body>
                    </CollapsibleCard>
                )}

                {/* ── Card 4: Passengers ── */}
                {incident.passengers?.length > 0 && (
                    <CollapsibleCard
                        color={headerColor}
                        icon={MdPeople}
                        title={`Passengers (${incident.passengers.length})`}
                        defaultExpanded={false}
                    >
                        <Card.Body style={{ padding: 0 }}>
                            <div style={tableContainerStyle}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Name</th>
                                            <th style={thStyle}>Seat</th>
                                            <th style={thStyle}>Assistance Requirements</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {incident.passengers.map((p, i) => (
                                            <tr key={i}>
                                                <td style={{ ...tdStyle, fontWeight: 500 }}>
                                                    {[p.firstName, p.lastName].filter(Boolean).join(' ') || '—'}
                                                </td>
                                                <td style={tdStyle}>{p.seatNumber || '—'}</td>
                                                <td style={tdStyle}>{p.assistanceRequirements || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                    </CollapsibleCard>
                )}
            </>)}

            {/* ── Card 5: Attachments (auto-folded, conditional) ── */}
            {incident.attachments?.length > 0 && (
                <CollapsibleCard
                    color={headerColor}
                    icon={MdAttachFile}
                    title={`Attachments (${incident.attachments.length})`}
                    defaultExpanded={false}
                >
                    <Card.Body style={CONTENT_STYLE}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {incident.attachments.map((att) => (
                                <a
                                    key={att.id}
                                    href={att.downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: 6,
                                        fontSize: 13,
                                        color: '#3b82f6',
                                        textDecoration: 'none',
                                        backgroundColor: '#f9fafb',
                                        transition: 'all 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#eff6ff';
                                        e.currentTarget.style.borderColor = '#93c5fd';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                        e.currentTarget.style.borderColor = '#d1d5db';
                                    }}
                                >
                                    <MdAttachFile size={14} />
                                    {att.filename}
                                </a>
                            ))}
                        </div>
                    </Card.Body>
                </CollapsibleCard>
            )}

            {/* ── Card 6: Acknowledgements (expanded by default) ── */}
            <CollapsibleCard
                color={headerColor}
                icon={MdPeople}
                title={`Acknowledgements (${ackEntries.length})`}
                defaultExpanded
            >
                <Card.Body style={{ padding: 0 }}>
                    {ackEntries.length === 0 ? (
                        <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                            No employees have been assigned to this incident yet.
                        </div>
                    ) : (
                        <div style={tableContainerStyle}>
                            <table style={tableStyle}>
                                <colgroup>
                                    <col style={{ width: '22%' }} />
                                    <col style={{ width: '14%' }} />
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '46%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Employee</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}>Acknowledged At</th>
                                        <th style={thStyle}>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ackEntries.map(([empId, ack]) => {
                                        const name = employeeNameMap[empId];
                                        return (
                                            <tr key={empId}>
                                                <td style={{ ...tdStyle, fontWeight: 500 }}>
                                                    {name ? (
                                                        <>
                                                            <div>{name}</div>
                                                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                                                                {empId}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        empId
                                                    )}
                                                </td>
                                                <td style={tdStyle}><StatusBadge status={ack.status} ack /></td>
                                                <td style={{ ...tdStyle, color: '#6b7280' }}>{formatDateTime(ack.acknowledgedAt)}</td>
                                                <td style={{ ...tdStyle, color: ack.notes ? '#374151' : '#9ca3af', fontStyle: ack.notes ? 'normal' : 'italic', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                                    {ack.notes ?? 'No notes provided'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </CollapsibleCard>

            {/* ── Reviewer Comments (returned-to-agent feedback trail) ── */}
            {sortedComments.length > 0 && (
                <CollapsibleCard
                    color={headerColor}
                    icon={MdComment}
                    title={`Reviewer Comments (${sortedComments.length})`}
                    defaultExpanded
                >
                    <Card.Body style={CONTENT_STYLE}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {sortedComments.map((c, i) => (
                                <div
                                    key={c.id ?? i}
                                    style={{
                                        border: '1px solid #FCA5A5',
                                        borderLeft: '4px solid #DC2626',
                                        borderRadius: 6,
                                        backgroundColor: '#FEF2F2',
                                        padding: '10px 14px',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                                            {c.author?.name || c.author?.email || 'Reviewer'}
                                        </span>
                                        <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                                            {formatDateTime(c.timestamp)}
                                        </span>
                                    </div>
                                    <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                        {c.content}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </CollapsibleCard>
            )}

            {/* ── Card 7: Events Log (status-change audit trail) ── */}
            {sortedEvents.length > 0 &&
                <CollapsibleCard
                    color={headerColor}
                    icon={MdHistory}
                    title={`Events Log (${sortedEvents.length})`}
                    defaultExpanded={false}
                >
                    <Card.Body style={{ padding: 0 }}>
                        <div style={tableContainerStyle}>
                            <table style={tableStyle}>
                                <colgroup>
                                    <col style={{ width: '30%' }} />
                                    <col style={{ width: '40%' }} />
                                    <col style={{ width: '30%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}>Changed By</th>
                                        <th style={thStyle}>When</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedEvents.map((ev, i) => (
                                        <tr key={i}>
                                            <td style={tdStyle}><StatusBadge status={ev.status} /></td>
                                            <td style={{ ...tdStyle, fontWeight: 500 }}>
                                                {ev.author?.name ? (
                                                    <>
                                                        <div>{ev.author.name}</div>
                                                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                                                            {ev.author.email}
                                                        </div>
                                                    </>
                                                ) : (
                                                    ev.author?.email || '—'
                                                )}
                                            </td>
                                            <td style={{ ...tdStyle, color: '#6b7280' }}>{formatDateTime(ev.timestamp)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </Card.Body>
                </CollapsibleCard>

            }

            {/* Send-back comment box */}
            {showReturn && (
                <div style={{
                    border: '1px solid #FCA5A5',
                    borderRadius: 8,
                    backgroundColor: '#FEF2F2',
                    padding: 16,
                    marginTop: 8,
                }}>
                    {/* Recipient picker — only when more than one agent is assigned */}
                    {assignedEmployees.length > 1 && (
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ ...LABEL_STYLE, color: '#991B1B', marginBottom: 8 }}>
                                Send back to
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {assignedEmployees.map((empId) => {
                                    const selected = returnRecipients.includes(empId);
                                    return (
                                        <label
                                            key={empId}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                padding: '6px 12px',
                                                borderRadius: 20,
                                                border: `1px solid ${selected ? '#DC2626' : '#d1d5db'}`,
                                                backgroundColor: selected ? '#FEE2E2' : '#fff',
                                                color: selected ? '#991B1B' : '#374151',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => toggleRecipient(empId)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                            {employeeNameMap[empId] || empId}
                                        </label>
                                    );
                                })}
                            </div>
                            {returnRecipients.length === 0 && (
                                <div style={{ fontSize: 12, color: '#991B1B', marginTop: 6 }}>
                                    Select at least one agent to send the report back to.
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ ...LABEL_STYLE, color: '#991B1B', marginBottom: 8 }}>
                        Reason for sending back to agent
                    </div>
                    <textarea
                        value={returnComment}
                        onChange={(e) => setReturnComment(e.target.value)}
                        placeholder="Explain why the report has not been accepted, e.g. not enough information…"
                        rows={3}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 6,
                            fontSize: 14,
                            color: '#374151',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                        }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                        <Button
                            variant="outline-secondary"
                            onClick={() => { setShowReturn(false); setReturnComment(''); }}
                            style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleReturn}
                            disabled={returning || !returnComment.trim() || returnRecipients.length === 0}
                            style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 600 }}
                        >
                            {returning ? 'Sending…' : '↩ Send Back to Agent'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Bottom action bar */}
            {editing ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 10,
                    flexWrap: 'wrap',
                    padding: '20px 0',
                    marginTop: 8,
                    borderTop: '1px solid #e5e7eb',
                }}>
                    {saveError && (
                        <span style={{ fontSize: 13, color: '#C0392B', fontWeight: 500, marginRight: 'auto' }}>
                            {saveError}
                        </span>
                    )}
                    <Button
                        variant="outline-secondary"
                        onClick={cancelEdit}
                        disabled={saving}
                        style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveEdit}
                        disabled={saving}
                        style={{ borderRadius: 8, padding: '8px 20px', fontSize: 14, fontWeight: 600 }}
                    >
                        {saving ? 'Saving…' : 'Save Changes'}
                    </Button>
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 10,
                    flexWrap: 'wrap',
                    padding: '20px 0',
                    marginTop: 8,
                    borderTop: '1px solid #e5e7eb',
                }}>
                    {!confirmDelete ? (
                        <Button
                            variant="outline-danger"
                            onClick={() => setConfirmDelete(true)}
                            style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                        >
                            Delete
                        </Button>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 500 }}>
                                Are you sure?
                            </span>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                disabled={deleting}
                                style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                            >
                                {deleting ? 'Deleting…' : 'Yes, delete'}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setConfirmDelete(false)}
                                style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                    {incident.status !== 'closed' && !showReturn && (
                        <Button
                            variant="outline-danger"
                            onClick={openReturn}
                            style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                        >
                            ↩ Send Back to Agent
                        </Button>
                    )}
                    {incident.status !== 'closed' && incident.status !== 'in_progress' && !showReturn && (
                        <Button
                            variant="outline-primary"
                            onClick={handleAcknowledge}
                            disabled={acknowledging}
                            style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                        >
                            {acknowledging ? 'Saving…' : '✓ Acknowledge'}
                        </Button>
                    )}
                    {incident.status !== 'closed' && (
                        <Button
                            variant="success"
                            onClick={handleClose}
                            disabled={closing}
                            style={{ borderRadius: 8, padding: '8px 20px', fontSize: 14, fontWeight: 600 }}
                        >
                            {closing ? 'Saving…' : '✓✓ Mark as Complete'}
                        </Button>
                    )}
                    {incident.status === 'closed' && (
                        <Button
                            variant="outline-secondary"
                            onClick={handleUnapprove}
                            disabled={unapproving}
                            style={{ borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 500 }}
                        >
                            {unapproving ? 'Reverting…' : 'Unapprove'}
                        </Button>
                    )}
                </div>
            )}

            {/* Bottom spacing */}
            <div style={{ height: 64 }} />
        </>
    );
};

export default IncidentDetail;
